import {
  Alert,
  Button,
  Code,
  CopyButton,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useCallback, useEffect, useState } from "react";

import { ApiError, api, type RecentCert } from "../lib/api";

/**
 * The mobile-responsive readback panel from plan §3.13. After a `dovach-op
 * sign` ceremony, open this on your phone to verify what landed in the
 * cloud matches what you just approved on the laptop. This is the single
 * most important UX element for the on-disk-key trust model — it catches
 * the residual host-substitution risk where laptop malware could swap
 * cert bytes between approval and publish.
 *
 * Mobile-first: large fingerprint copy buttons, single column on small
 * screens, dense table on desktop.
 */
export function CertsReadbackPanel() {
  const [certs, setCerts] = useState<RecentCert[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const resp = await api.recentCerts();
      setCerts(resp.certs);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "fetch failed");
    }
  }, []);

  useEffect(() => {
    refresh();
    const i = window.setInterval(refresh, 10000);
    return () => window.clearInterval(i);
  }, [refresh]);

  if (error) return <Alert color="red">{error}</Alert>;
  if (certs === null) return <Loader size="sm" />;

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Recently signed certs</Title>
        <Button variant="light" onClick={refresh}>
          Refresh
        </Button>
      </Group>
      <Text c="dimmed">
        Second-device readback. Fingerprints below should match what your
        operator CLI just printed. Mismatch = host substitution attack —
        revoke immediately.
      </Text>

      {certs.length === 0 ? (
        <Paper p="xl" withBorder>
          <Text c="dimmed">No signed certs yet. After running <code>dovach-op sign</code>, refresh.</Text>
        </Paper>
      ) : (
        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Account</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Office pubkey</Table.Th>
                <Table.Th>Fingerprint (BLAKE2b-160)</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {certs.map((c) => (
                <Table.Tr key={c.account_id}>
                  <Table.Td>
                    <Code>{c.account_id.slice(0, 12)}…</Code>
                  </Table.Td>
                  <Table.Td>{c.email}</Table.Td>
                  <Table.Td>
                    <Code>{c.office_pk.slice(0, 16)}…</Code>
                  </Table.Td>
                  <Table.Td>
                    <FingerprintWithCopy value={c.fingerprint} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}

function FingerprintWithCopy({ value }: { value: string }) {
  const display =
    value.length === 40
      ? `${value.slice(0, 8)}…${value.slice(-8)}`
      : value;
  return (
    <CopyButton value={value}>
      {({ copied, copy }) => (
        <Group gap={6}>
          <Code style={{ fontSize: 14 }}>{display}</Code>
          <Button
            size="xs"
            variant={copied ? "filled" : "light"}
            color={copied ? "green" : "blue"}
            onClick={copy}
          >
            {copied ? "Copied" : "Copy"}
          </Button>
        </Group>
      )}
    </CopyButton>
  );
}
